// ─── Access gate for hosted 3D tools ─────────────────────────────────────────
// Imported by the pergola (src/index.js) and kitchen (src/kitchen/index.js)
// entries; the awning tool is deliberately NOT gated.
//
// This is a client-side gate for a static host (GitHub Pages / rsync'd VPS
// files) — it keeps the tools out of casual public view. Credentials are
// never stored in the source; entry is verified against a SHA-256 hash of
// "user:password". It is not server-enforced security: someone technical
// could bypass it. If server-grade protection is ever needed, add HTTP basic
// auth in the VPS web-server config instead.

const GATE_HASH = '8c9f6ff0b48e70e307610e4702bed1011388916459034e377894897973db6c34';
const STORE_KEY = 'breslow3d.gate';

async function sha256Hex(str) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

(function gate() {
  // Embedded builds (e.g. the EasyOps /3d-sg-pro artifact) disable the gate at
  // build time — inside EasyOps the page is a rep tool with pre-auth share
  // links, same as /3d-pro, and a password prompt would break customer links.
  if (import.meta.env && import.meta.env.VITE_DISABLE_GATE === '1') return;
  try {
    if (localStorage.getItem(STORE_KEY) === GATE_HASH) return;
  } catch { /* storage blocked — always prompt */ }

  const overlay = document.createElement('div');
  overlay.id = 'gate-overlay';
  overlay.innerHTML = `
    <style>
      #gate-overlay {
        position: fixed; inset: 0; z-index: 10000;
        background: #101210;
        display: flex; align-items: center; justify-content: center;
        font-family: -apple-system, 'Segoe UI', Roboto, sans-serif;
      }
      #gate-overlay .gate-card {
        background: #181a18; border: 1px solid #2c2c2c; border-radius: 10px;
        padding: 34px 38px; width: 320px; text-align: center;
      }
      #gate-overlay h1 { color: #e8e8e4; font-size: 17px; margin: 0 0 4px; letter-spacing: 0.4px; }
      #gate-overlay p  { color: #7d7d76; font-size: 11.5px; margin: 0 0 20px; }
      #gate-overlay input {
        width: 100%; box-sizing: border-box; margin-bottom: 10px;
        background: #101210; border: 1px solid #2c2c2c; border-radius: 6px;
        color: #ddd; padding: 10px 12px; font-size: 13px; outline: none;
      }
      #gate-overlay input:focus { border-color: #4d7c4d; }
      #gate-overlay button {
        width: 100%; background: #1c2b1c; border: 1px solid #4d7c4d; color: #9fd49f;
        border-radius: 6px; padding: 10px; font-size: 13px; cursor: pointer; margin-top: 4px;
      }
      #gate-overlay button:hover { background: #223522; }
      #gate-overlay .gate-err { color: #d9827a; font-size: 11px; height: 14px; margin-top: 10px; }
    </style>
    <div class="gate-card">
      <h1>BRESLOW&reg; 3D Tools</h1>
      <p>This tool is private. Sign in to continue.</p>
      <form id="gate-form">
        <input type="text" id="gate-user" placeholder="Username" autocomplete="username" autofocus>
        <input type="password" id="gate-pass" placeholder="Password" autocomplete="current-password">
        <button type="submit">Unlock</button>
        <div class="gate-err" id="gate-err"></div>
      </form>
    </div>`;

  const mount = () => {
    document.body.appendChild(overlay);
    overlay.querySelector('#gate-form').addEventListener('submit', async (e) => {
      e.preventDefault();
      const user = overlay.querySelector('#gate-user').value.trim();
      const pass = overlay.querySelector('#gate-pass').value;
      const hash = await sha256Hex(`${user}:${pass}`);
      if (hash === GATE_HASH) {
        try { localStorage.setItem(STORE_KEY, hash); } catch { /* fine */ }
        overlay.remove();
      } else {
        overlay.querySelector('#gate-err').textContent = 'Incorrect username or password.';
        overlay.querySelector('#gate-pass').value = '';
      }
    });
  };

  if (document.body) mount();
  else document.addEventListener('DOMContentLoaded', mount);
})();
